// components/GestionCodigosDescuento.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,
  Stack,
  Switch,
  FormControlLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalOffer as DiscountIcon,
} from '@mui/icons-material';

import {
  getAllDiscountCodes,
  createDiscountCode,
  updateDiscountCode,
  deleteDiscountCode,
} from '../../../services/discountCodeService';

export function GestionCodigosDescuento() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // 🆕 SIMPLIFICADO: Solo 5 campos
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    finalPrice: '',
    validFrom: '',
    validUntil: '',
    applicableTo: 'all',
    active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const codesData = await getAllDiscountCodes();
      setCodes(codesData.discountCodes || []);
    } catch (err) {
      setError('Error cargando códigos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (code = null) => {
    if (code) {
      // Modo edición
      setEditingCode(code);
      setFormData({
        code: code.code,
        description: code.description,
        finalPrice: code.finalPrice,
        validFrom: code.validFrom ? new Date(code.validFrom).toISOString().split('T')[0] : '',
        validUntil: code.validUntil ? new Date(code.validUntil).toISOString().split('T')[0] : '',
        applicableTo: code.applicableTo || 'all',
        active: code.active,
      });
    } else {
      // Modo creación
      setEditingCode(null);
      setFormData({
        code: '',
        description: '',
        finalPrice: '',
        validFrom: '',
        validUntil: '',
        applicableTo: 'all',
        active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCode(null);
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    try {
      // Validaciones simples
      if (!formData.code || !formData.description || !formData.finalPrice || !formData.validFrom || !formData.validUntil) {
        setError('Por favor completa todos los campos requeridos (*)');
        return;
      }

      if (formData.finalPrice <= 0) {
        setError('El precio final debe ser mayor a 0');
        return;
      }

      const dataToSend = {
        ...formData,
        finalPrice: Number(formData.finalPrice),
      };

      if (editingCode) {
        // Actualizar
        await updateDiscountCode(editingCode._id, dataToSend);
        setSuccess('Código actualizado exitosamente');
      } else {
        // Crear
        await createDiscountCode(dataToSend);
        setSuccess('Código creado exitosamente');
      }

      handleCloseDialog();
      loadData();
    } catch (err) {
      setError(err.message || 'Error guardando código');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este código de descuento?')) {
      return;
    }

    try {
      await deleteDiscountCode(id);
      setSuccess('Código eliminado exitosamente');
      loadData();
    } catch (err) {
      setError('Error eliminando código: ' + err.message);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setSuccess(`Código "${code}" copiado al portapapeles`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-MX');
  };

  const formatMXN = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DiscountIcon /> Códigos de Descuento para Bodas
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Nuevo Código
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Información */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Sistema simplificado para bodas:</strong> Cada código establece un precio final fijo para 2 noches.
          Ejemplo: Standard Suite (5760 MXN) → con código BODA2025 = 5150 MXN
        </Alert>

        {/* Tabla de códigos */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Precio Final</TableCell>
                <TableCell>Validez</TableCell>
                <TableCell>Aplicable a</TableCell>
                <TableCell>Usos</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.map((code) => (
                <TableRow key={code._id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
                        {code.code}
                      </Typography>
                      <IconButton size="small" onClick={() => handleCopyCode(code.code)} title="Copiar código">
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                  <TableCell>{code.description}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatMXN(code.finalPrice)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      para 2 noches
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(code.validFrom)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      hasta {formatDate(code.validUntil)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={code.applicableTo === 'all' ? 'Todas' : 
                             code.applicableTo === 'casaHotel' ? 'Casa Hotel' : 'Boutique'}
                      size="small"
                      variant="outlined"
                      color={code.applicableTo === 'all' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{code.uses || 0} usos</Typography>
                  </TableCell>
                  <TableCell>
                    {code.active ? (
                      <Chip label="Activo" color="success" size="small" icon={<CheckCircleIcon />} />
                    ) : (
                      <Chip label="Inactivo" color="default" size="small" icon={<CancelIcon />} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(code)} title="Editar">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(code._id)} color="error" title="Eliminar">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {codes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box py={4}>
                      <DiscountIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No hay códigos de descuento creados
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        sx={{ mt: 2 }}
                      >
                        Crear primer código
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 🆕 SIMPLIFICADO: Dialog con solo 5 campos */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCode ? 'Editar Código de Descuento' : 'Nuevo Código para Bodas'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Stack spacing={2}>
              {/* Código */}
              <TextField
                label="Código *"
                fullWidth
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                disabled={!!editingCode}
                helperText="Ej: BODA2025, VERANO20"
                required
              />

              {/* Descripción */}
              <TextField
                label="Descripción *"
                fullWidth
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                helperText="Ej: Precio especial para bodas - 2 noches"
                required
              />

              {/* Precio Final */}
              <TextField
                label="Precio Final Fijo *"
                fullWidth
                type="number"
                value={formData.finalPrice}
                onChange={(e) => setFormData({ ...formData, finalPrice: e.target.value })}
                InputProps={{ inputProps: { min: 0 } }}
                helperText="Precio final para 2 noches (ya incluye impuestos)"
                required
              />

              {/* Fechas */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Válido desde *"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={formData.validFrom}
                    onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Válido hasta *"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={formData.validUntil}
                    onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                    required
                  />
                </Grid>
              </Grid>

              {/* Aplicable a */}
              <FormControl fullWidth>
                <InputLabel>Aplicable a</InputLabel>
                <Select
                  value={formData.applicableTo}
                  onChange={(e) => setFormData({ ...formData, applicableTo: e.target.value })}
                  label="Aplicable a"
                >
                  <MenuItem value="all">Todas las habitaciones</MenuItem>
                  <MenuItem value="casaHotel">Solo Casa Hotel</MenuItem>
                  <MenuItem value="boutique">Solo Boutique</MenuItem>
                </Select>
              </FormControl>

              {/* Estado */}
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                }
                label="Código activo"
              />
            </Stack>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCode ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}